export class DefaultAudioStreamer {
    constructor(deps) {
        this.createObjectUrl = deps?.createObjectUrl ?? ((blob) => URL.createObjectURL(blob));
    }
    async streamToUrl(stream, mimeType) {
        // Create progressive streaming URL that starts playing immediately
        return this.createProgressiveStreamingUrl(stream, mimeType);
    }
    bufferToUrl(buffer, mimeType) {
        const blob = new Blob([buffer], { type: mimeType ?? 'audio/mpeg' });
        return this.createObjectUrl(blob);
    }
    /**
     * Create progressive streaming URL that can start playing immediately while data is still arriving
     */
    async createProgressiveStreamingUrl(stream, mimeType) {
        // Try MediaSource for true streaming if supported
        if (typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg')) {
            return this.createMediaSourceUrl(stream);
        }
        // Fallback: Buffer complete stream for compatibility
        return this.createCompleteBufferUrl(stream, mimeType);
    }
    /**
     * Create MediaSource URL for true streaming audio playback
     */
    async createMediaSourceUrl(stream) {
        const mediaSource = new MediaSource();
        const url = URL.createObjectURL(mediaSource);
        mediaSource.addEventListener('sourceopen', async () => {
            try {
                const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
                const reader = stream.getReader();
                const processStream = async () => {
                    try {
                        while (true) {
                            const { done, value } = await reader.read();
                            if (done) {
                                // Wait for any pending updates before ending
                                if (!sourceBuffer.updating) {
                                    mediaSource.endOfStream();
                                }
                                else {
                                    sourceBuffer.addEventListener('updateend', () => {
                                        if (!sourceBuffer.updating && mediaSource.readyState === 'open') {
                                            mediaSource.endOfStream();
                                        }
                                    }, { once: true });
                                }
                                break;
                            }
                            if (value) {
                                // Wait for buffer to be ready
                                if (sourceBuffer.updating) {
                                    await new Promise(resolve => {
                                        sourceBuffer.addEventListener('updateend', resolve, { once: true });
                                    });
                                }
                                if (!sourceBuffer.updating && mediaSource.readyState === 'open') {
                                    sourceBuffer.appendBuffer(value);
                                }
                            }
                        }
                    }
                    catch {
                        if (mediaSource.readyState === 'open') {
                            mediaSource.endOfStream('decode');
                        }
                    }
                    finally {
                        reader.releaseLock();
                    }
                };
                processStream();
            }
            catch {
                if (mediaSource.readyState === 'open') {
                    mediaSource.endOfStream('decode');
                }
            }
        });
        return url;
    }
    /**
     * Fallback: Create URL from complete buffered stream
     */
    async createCompleteBufferUrl(stream, mimeType) {
        const buffer = await this.readStreamToArrayBuffer(stream);
        return this.bufferToUrl(buffer, mimeType);
    }
    async readStreamToArrayBuffer(stream) {
        const reader = stream.getReader();
        const chunks = [];
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            if (value)
                chunks.push(value);
        }
        const totalLength = chunks.reduce((sum, c) => sum + c.byteLength, 0);
        const result = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
            result.set(chunk, offset);
            offset += chunk.byteLength;
        }
        return result.buffer;
    }
}
